"use client";

import React, { useEffect, useMemo, useState } from "react";
import { OrderListItem, PAYMENT_METHOD, IS_DEBIT, ORDER_STATUS } from "../services/order-service";
import DateInputCustom from "../../../components/custom/date-input/date-input-custom";
import SelectDropdownAsyncPaginate from "../../../components/custom/select-dropdown-async-paginate";
import PickupStaffSelect from "../../../components/custom/pickup-staff-select";
import { ApiConfig, API_USER_ROUTER, API_BRANCH_ROUTER } from "../../../services/api-config";
import { useFetchData } from "../../../hooks/useFetch";
import { isSuccess } from "../../../models/response/base-response";
import BranchItemsPicker, { PickedItem } from "./BranchItemsPicker";
import OrderItemsEditor from "./OrderItemsEditor";
import VehicleLegPicker from "../../../components/custom/vehicle-leg-picker";
import { formatKg } from "../../../utils/helpers";

interface EmployeeOption {
  id: number;
  name?: string;
  phone?: string;
  username?: string;
}

interface OrderUpdateModalProps {
  open: boolean;
  order: OrderListItem | null;
  onClose: () => void;
  onSave: (
    id: number,
    data: {
      address: string;
      note: string;
      pickup_driver_ids?: number[];
      employee_ids?: number[];
      delivery_driver_ids?: number[];
      washer_ids?: number[];
      dryer_ids?: number[];
      ironer_ids?: number[];
      folder_ids?: number[];
      scheduled_delivery_at?: string | null;
      pickup_weight?: number;
      final_weight?: number;
      payment_method?: number;
      is_debit?: number;
      branch_id?: number;
      items?: { item_id: number; item_type: number; quantity: number }[];
      /** Xe tải đi thẳng theo chiều — null = bỏ xe (xe máy). */
      pickup_vehicle_id?: number | null;
      delivery_vehicle_id?: number | null;
    }
    // Trả `false` khi lưu thất bại → modal ở lại để người dùng sửa (vd đổi chi nhánh bị BE từ chối).
  ) => Promise<void | boolean>;
  loading?: boolean;
}

const OrderUpdateModal: React.FC<OrderUpdateModalProps> = ({
  open,
  order,
  onClose,
  onSave,
  loading = false,
}) => {
  const [address, setAddress] = useState(order?.address ?? "");
  const [note, setNote] = useState(order?.note ?? "");
  const [scheduledDeliveryAt, setScheduledDeliveryAt] = useState("");

  // Multiple employee selections
  const [selectedPickups, setSelectedPickups] = useState<EmployeeOption[]>([]);
  const [selectedEmployees, setSelectedEmployees] = useState<EmployeeOption[]>([]);
  const [selectedDeliveries, setSelectedDeliveries] = useState<EmployeeOption[]>([]);
  // Đơn kiểm đếm: NV kiểm đếm khi NHẬN (role 9) + khi GIAO (role 12)
  const [selectedCounters, setSelectedCounters] = useState<EmployeeOption[]>([]);
  const [selectedDeliveryCounters, setSelectedDeliveryCounters] = useState<EmployeeOption[]>([]);
  // Xe tải đi thẳng theo chiều (null = xe máy). Có xe → người của chiều = đội hình xe (ô chọn tài xế ẩn).
  const [pickupVehicleId, setPickupVehicleId] = useState<number | null>(null);
  const [deliveryVehicleId, setDeliveryVehicleId] = useState<number | null>(null);

  // Extended workflow employees
  const [selectedWashers, setSelectedWashers] = useState<EmployeeOption[]>([]);
  const [selectedDryers, setSelectedDryers] = useState<EmployeeOption[]>([]);
  const [selectedIroners, setSelectedIroners] = useState<EmployeeOption[]>([]);
  const [selectedFolders, setSelectedFolders] = useState<EmployeeOption[]>([]);

  // Weight fields
  const [pickupWeight, setPickupWeight] = useState<string>("");
  const [finalWeight, setFinalWeight] = useState<string>("");

  // Payment fields
  const [paymentMethod, setPaymentMethod] = useState<number>(PAYMENT_METHOD.CASH);
  const [isDebit, setIsDebit] = useState<number>(IS_DEBIT.NO_DEBIT);

  // Chi nhánh — chỉ sửa được khi đơn còn "Đợi xác nhận" (dùng cho trường hợp tạo nhầm chi nhánh).
  // Sau mốc đó đơn đã có kiểm đếm/thiết bị/điều chuyển gắn theo chi nhánh nên BE cũng chặn.
  const [branchId, setBranchId] = useState<number>(0);
  const [branches, setBranches] = useState<{ id: number; name: string }[]>([]);
  // Dịch vụ chọn lại theo chi nhánh mới (bắt buộc khi đổi chi nhánh — xem BranchItemsPicker).
  const [branchItems, setBranchItems] = useState<PickedItem[]>([]);
  const [branchItemsReady, setBranchItemsReady] = useState(false);
  // Sửa sản phẩm / dịch vụ khi KHÔNG đổi chi nhánh (xem OrderItemsEditor) — chỉ gửi khi có thay đổi thật.
  const [editedItems, setEditedItems] = useState<PickedItem[]>([]);
  const [editedItemsReady, setEditedItemsReady] = useState(false);
  const [editedItemsDirty, setEditedItemsDirty] = useState(false);
  const { request: getBranches } = useFetchData.Get<any>({ projectId: ApiConfig.PROJECT_ID.USER_SERVICE });

  // (Danh sách NV nhận/giao + số đơn treo đã chuyển vào component PickupStaffSelect)

  const isExtended = order?.workflow_type === 1;
  const isCounting = Number((order as any)?.requires_counting) === 1;
  const canEditBranch = Number(order?.status) === ORDER_STATUS.WAITING_ACCEPT;
  // Chiều còn "chưa bắt đầu" mới đổi xe được — khớp BE `isVehicleLegOpen` (xe-tai-doi-hinh-ky-thuat.md §7.2).
  const orderStatus = Number(order?.status);
  const pickupLegOpen = isCounting
    ? [ORDER_STATUS.WAITING_ACCEPT, ORDER_STATUS.COUNTING, ORDER_STATUS.COUNTING_DONE, ORDER_STATUS.WAITING_DRIVER].includes(orderStatus)
    : orderStatus === ORDER_STATUS.WAITING_ACCEPT;
  const deliveryLegOpen = ![
    ORDER_STATUS.IN_DELIVERY, ORDER_STATUS.DELIVERY_COUNTING, ORDER_STATUS.COMPLETE, ORDER_STATUS.CANCELED,
  ].includes(orderStatus);
  const branchChanged = canEditBranch && branchId > 0 && branchId !== Number(order?.branch_id);
  // Sửa sản phẩm / dịch vụ: đơn chưa hoàn tất / chưa huỷ, không phải đơn cha–con hay đơn giặt lại — khớp BE
  // `planItemsChange`. Đang đổi chi nhánh thì dùng BranchItemsPicker (chọn lại toàn bộ theo chi nhánh mới).
  const canEditItems =
    ![ORDER_STATUS.COMPLETE, ORDER_STATUS.CANCELED].includes(orderStatus) &&
    Number(order?.has_sub_orders) !== 1 &&
    !Number(order?.parent_order_id) &&
    Number(order?.order_type) !== 2;
  const itemsChanged = canEditItems && !branchChanged && editedItemsDirty;

  // Danh sách NV để phân công lấy theo chi nhánh ĐANG GIỮ ĐỒ (đơn điều chuyển: current_branch_id), không phải chủ đơn
  // — khớp BE (người được gán ghi chi nhánh đang giữ đồ). Đang đổi chi nhánh → theo chi nhánh mới.
  const staffBranchId: number | null = branchChanged
    ? branchId
    : Number((order as any)?.current_branch_id) || Number(order?.branch_id) || null;

  // Đơn điều chuyển: vai đã làm XONG ở chi nhánh KHÁC chi nhánh đang giữ đồ → khoá (BE cũng bỏ qua thay đổi của
  // các vai này — `lockedRoles` trong updateOrderInfo). Người được gán sẵn mà chưa làm (VD tài xế giao gán lúc tạo đơn
  // ở cửa hàng gốc) không khoá — chi nhánh đang giữ đồ đổi được. role_type → tên chi nhánh đã thực hiện.
  const lockedRoles = useMemo(() => {
    const map = new Map<number, string>();
    if (!order || !Number((order as any).has_transfer)) return map;
    const ownerBranch = Number(order.branch_id);
    const custody = Number((order as any).current_branch_id) || ownerBranch;
    for (const e of ((order as any).employees || []) as any[]) {
      if (Number(e.status) !== 4) continue; // chỉ dòng COMPLETED
      const b = Number(e.branch_id) || ownerBranch;
      const role = Number(e.role_type);
      if (b === custody || map.has(role)) continue;
      map.set(role, e.branch_name || (b === ownerBranch ? order.branch?.name : "") || `CN #${b}`);
    }
    return map;
  }, [order]);
  const isLocked = (role: number) => lockedRoles.has(role);
  const lockHint = (role: number) =>
    lockedRoles.has(role) ? (
      <p className="mt-1 text-xs text-slate-400">Đã thực hiện ở {lockedRoles.get(role)} — không chỉnh được</p>
    ) : null;

  // Sync data when modal opens for a different order
  useEffect(() => {
    if (open && order) {
      setAddress(order.address ?? "");
      setNote(order.note ?? "");
      if (order.scheduled_delivery_at) {
        const d = new Date(order.scheduled_delivery_at);
        const dd = d.getDate().toString().padStart(2, "0");
        const MM = (d.getMonth() + 1).toString().padStart(2, "0");
        const yyyy = d.getFullYear();
        const hh = d.getHours().toString().padStart(2, "0");
        const mm = d.getMinutes().toString().padStart(2, "0");
        setScheduledDeliveryAt(`${dd}/${MM}/${yyyy} ${hh}:${mm}`);
      } else {
        setScheduledDeliveryAt("");
      }

      // Sync existing employees from order
      const pickupEmployees: EmployeeOption[] = [];
      const laundryEmployees: EmployeeOption[] = [];
      const deliveryEmployees: EmployeeOption[] = [];
      const washerEmployees: EmployeeOption[] = [];
      const dryerEmployees: EmployeeOption[] = [];
      const ironerEmployees: EmployeeOption[] = [];
      const folderEmployees: EmployeeOption[] = [];
      const counterEmployees: EmployeeOption[] = [];         // role 9 — kiểm đếm khi nhận
      const deliveryCounterEmployees: EmployeeOption[] = [];  // role 12 — kiểm đếm khi giao

      // From legacy single driver fields
      if (order.driverPickup?.id) {
        pickupEmployees.push({
          id: order.driverPickup.id,
          name: order.driverPickup.name,
          phone: order.driverPickup.phone,
        });
      }
      if (order.employee?.id) {
        laundryEmployees.push({
          id: order.employee.id,
          name: order.employee.name,
          phone: order.employee.phone,
        });
      }
      if (order.driverDelivery?.id) {
        deliveryEmployees.push({
          id: order.driverDelivery.id,
          name: order.driverDelivery.name,
          phone: order.driverDelivery.phone,
        });
      }

      // From new employees array
      // role_type: 1=pickup, 2=laundry, 3=delivery, 5=washer, 6=dryer, 7=ironer, 8=folder
      // status: 1=assigned, 2=accepted, 3=rejected, 4=completed
      if (order.employees && order.employees.length > 0) {
        for (const emp of order.employees) {
          // Bỏ qua nhân viên đã từ chối (REJECTED = 3)
          if (emp.status === 3) continue;
          const empOption: EmployeeOption = {
            id: emp.employee_id,
            name: emp.employee?.name,
          };
          switch (emp.role_type) {
            case 1:
              if (!pickupEmployees.some(e => e.id === emp.employee_id)) pickupEmployees.push(empOption);
              break;
            case 2:
              if (!laundryEmployees.some(e => e.id === emp.employee_id)) laundryEmployees.push(empOption);
              break;
            case 3:
              if (!deliveryEmployees.some(e => e.id === emp.employee_id)) deliveryEmployees.push(empOption);
              break;
            case 5:
              if (!washerEmployees.some(e => e.id === emp.employee_id)) washerEmployees.push(empOption);
              break;
            case 6:
              if (!dryerEmployees.some(e => e.id === emp.employee_id)) dryerEmployees.push(empOption);
              break;
            case 7:
              if (!ironerEmployees.some(e => e.id === emp.employee_id)) ironerEmployees.push(empOption);
              break;
            case 8:
              if (!folderEmployees.some(e => e.id === emp.employee_id)) folderEmployees.push(empOption);
              break;
            case 9:
              if (!counterEmployees.some(e => e.id === emp.employee_id)) counterEmployees.push(empOption);
              break;
            case 12:
              if (!deliveryCounterEmployees.some(e => e.id === emp.employee_id)) deliveryCounterEmployees.push(empOption);
              break;
          }
        }
      }

      setSelectedPickups(pickupEmployees);
      setSelectedEmployees(laundryEmployees);
      setSelectedDeliveries(deliveryEmployees);
      setSelectedWashers(washerEmployees);
      setSelectedDryers(dryerEmployees);
      setSelectedIroners(ironerEmployees);
      setSelectedFolders(folderEmployees);
      setSelectedCounters(counterEmployees);
      setSelectedDeliveryCounters(deliveryCounterEmployees);
      setPickupVehicleId(Number(order.pickup_vehicle_id) || null);
      setDeliveryVehicleId(Number(order.delivery_vehicle_id) || null);

      // Sync weight fields
      // DECIMAL trả chuỗi ("0.00", "12.50") — qua Number để ô nhập hiện "12.5", và 0 thì để trống (chưa cân).
      const toWeightInput = (v: unknown) => (Number(v) > 0 ? String(Number(v)) : "");
      setPickupWeight(toWeightInput(order.pickup_weight));
      setFinalWeight(toWeightInput(order.final_weight));

      // Sync payment fields
      setPaymentMethod(Number(order.payment_method) || PAYMENT_METHOD.CASH);
      setIsDebit(Number(order.is_debit) || IS_DEBIT.NO_DEBIT);
      setBranchId(Number(order.branch_id) || 0);
      setEditedItems([]);
      setEditedItemsReady(false);
      setEditedItemsDirty(false);
    }
  }, [open, order?.id]);

  // Danh sách chi nhánh cho ô "Chi nhánh": dùng my-branches vì BE chỉ cho đẩy đơn về chi nhánh mình có
  // quyền (chủ tài khoản thì my-branches trả về tất cả nên vẫn đổi được tự do).
  useEffect(() => {
    if (!open || !canEditBranch || branches.length > 0) return;
    let mounted = true;
    (async () => {
      try {
        const res = await getBranches({ endPoint: API_BRANCH_ROUTER.API_MY_BRANCHES });
        if (!mounted || !res || !isSuccess(res)) return;
        const raw: any = res.data;
        const list = Array.isArray(raw) ? raw : Array.isArray(raw?.data) ? raw.data : Array.isArray(raw?.list) ? raw.list : [];
        setBranches(list.map((b: any) => ({ id: Number(b.id), name: b.name })));
      } catch { /* không tải được → ô chi nhánh chỉ hiện chi nhánh hiện tại */ }
    })();
    return () => { mounted = false; };
    // eslint-disable-next-line
  }, [open, canEditBranch]);


  if (!open || !order) return null;

  const handleSave = async () => {
    if (!order?.id) return;
    const data: any = {
      address,
      note,
      pickup_driver_ids: selectedPickups.map(e => e.id),
      employee_ids: selectedEmployees.map(e => e.id),
      delivery_driver_ids: selectedDeliveries.map(e => e.id),
      scheduled_delivery_at: scheduledDeliveryAt,
      pickup_weight: pickupWeight ? parseFloat(pickupWeight) : undefined,
      final_weight: finalWeight ? parseFloat(finalWeight) : undefined,
      payment_method: isDebit === IS_DEBIT.DEBIT ? PAYMENT_METHOD.CONG_NO : paymentMethod,
      is_debit: isDebit,
    };
    if (branchChanged) {
      data.branch_id = branchId;
      data.items = branchItems; // BE bắt buộc có, vì id dịch vụ gắn theo chi nhánh
    } else if (itemsChanged) {
      data.items = editedItems; // danh sách đầy đủ sau khi sửa — BE tự so với dòng hiện có
    }
    if (isExtended) {
      data.washer_ids = selectedWashers.map(e => e.id);
      data.dryer_ids = selectedDryers.map(e => e.id);
      data.ironer_ids = selectedIroners.map(e => e.id);
      data.folder_ids = selectedFolders.map(e => e.id);
    }
    if (isCounting) {
      // Đơn kiểm đếm mới có 2 vai này (role 9 / 12). BE không set counting_staff_id — để runtime claim.
      data.counter_ids = selectedCounters.map(e => e.id);
      data.delivery_counter_ids = selectedDeliveryCounters.map(e => e.id);
    }
    // Xe tải đi thẳng: chiều có xe → người = đội hình xe, KHÔNG gửi danh sách tài xế của chiều đó.
    data.pickup_vehicle_id = pickupVehicleId;
    data.delivery_vehicle_id = deliveryVehicleId;
    if (pickupVehicleId) delete data.pickup_driver_ids;
    if (deliveryVehicleId) delete data.delivery_driver_ids;
    const ok = await onSave(order.id, data);
    if (ok !== false) onClose();
  };

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
      <div className="bg-white rounded-xl w-[600px] max-w-[95vw] p-6 shadow-2xl animate-fadeIn max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-slate-800">
            Cập nhật đơn hàng
          </h3>
          <button
            onClick={onClose}
            className="text-slate-500 hover:text-slate-700 text-lg"
          >
            ✕
          </button>
        </div>

        {/* Form Content */}
        <div className="space-y-4">
          {/* Chi nhánh — chỉ mở khi đơn còn "Đợi xác nhận" (sửa khi tạo nhầm chi nhánh) */}
          {canEditBranch && (
            <div>
              <label className="block text-sm text-slate-600 mb-1">Chi nhánh</label>
              <select
                value={branchId || ""}
                onChange={(e) => {
                  const next = Number(e.target.value) || 0;
                  setBranchId(next);
                  // Nhân sự thuộc chi nhánh cũ không còn hợp lệ — bỏ chọn hết để người dùng gán lại.
                  if (next !== Number(order?.branch_id)) {
                    setSelectedPickups([]);
                    setSelectedEmployees([]);
                    setSelectedDeliveries([]);
                    setSelectedWashers([]);
                    setSelectedDryers([]);
                    setSelectedIroners([]);
                    setSelectedFolders([]);
                    setSelectedCounters([]);
                    setSelectedDeliveryCounters([]);
                  }
                }}
                className="w-full border rounded-lg px-3 py-2 shadow-sm focus:outline-none focus:ring-1 focus:ring-green-500"
              >
                {branches.length === 0 && (
                  <option value={Number(order.branch_id) || ""}>
                    {order.branch?.name || `CN #${order.branch_id}`}
                  </option>
                )}
                {branches.map((b) => (
                  <option key={b.id} value={b.id}>{b.name}</option>
                ))}
              </select>
              {branchChanged && (
                <p className="mt-1 text-xs text-amber-600">
                  Đổi chi nhánh sẽ tính lại giá theo bảng giá chi nhánh mới và gỡ nhân sự không thuộc chi
                  nhánh đó. Chỉ đổi được khi đơn chưa được xác nhận.
                </p>
              )}
            </div>
          )}

          {branchChanged && (
            <BranchItemsPicker
              orderId={Number(order.id)}
              branchId={branchId}
              onChange={(items, ready) => {
                setBranchItems(items);
                setBranchItemsReady(ready);
              }}
            />
          )}

          {canEditItems && !branchChanged && (
            <OrderItemsEditor
              key={`items-${order.id}`}
              orderId={Number(order.id)}
              branchId={Number(order.branch_id)}
              onChange={(items, ready, dirty) => {
                setEditedItems(items);
                setEditedItemsReady(ready);
                setEditedItemsDirty(dirty);
              }}
            />
          )}

          <div>
            <label className="block text-sm text-slate-600 mb-1">Địa chỉ</label>
            <input
              type="text"
              value={address}
              onChange={(e) => setAddress(e.target.value)}
              className="w-full border rounded-lg px-3 py-2 shadow-sm focus:outline-none focus:ring-1 focus:ring-green-500"
              placeholder="Nhập địa chỉ giao hàng..."
            />
          </div>

          <div>
            <label className="block text-sm text-slate-600 mb-1">Ghi chú</label>
            <textarea
              value={note}
              onChange={(e) => setNote(e.target.value)}
              className="w-full border rounded-lg px-3 py-2 h-24 shadow-sm focus:outline-none focus:ring-1 focus:ring-green-500 resize-none"
              placeholder="Thêm ghi chú cho đơn hàng..."
            />
          </div>

          {/* Scheduled Delivery */}
          <div>
            <label className="block text-sm text-slate-600 mb-1">Hẹn giờ giao</label>
            <DateInputCustom
              value={scheduledDeliveryAt}
              onChange={(date) => setScheduledDeliveryAt(date ?? "")}
              placeholder="Chọn ngày giờ giao"
              type="date-time"
              className="w-full"
            />
          </div>

          {/* Payment Section */}
          <div className="bg-amber-50 rounded-lg p-4 border border-amber-100">
            <h4 className="text-sm font-semibold text-amber-800 mb-3">Thanh toán</h4>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-xs text-slate-500 mb-1">Hình thức thanh toán</label>
                <select
                  className="w-full border rounded-lg px-3 py-2 focus:outline-none focus:ring-1 focus:ring-amber-500"
                  value={paymentMethod}
                  onChange={(e) => setPaymentMethod(Number(e.target.value))}
                  disabled={isDebit === IS_DEBIT.DEBIT}
                >
                  <option value={PAYMENT_METHOD.CASH}>Tiền mặt</option>
                  <option value={PAYMENT_METHOD.QR_CODE}>Chuyển khoản</option>
                </select>
              </div>
              <div>
                <label className="block text-xs text-slate-500 mb-1">Ghi nợ</label>
                <select
                  className="w-full border rounded-lg px-3 py-2 focus:outline-none focus:ring-1 focus:ring-amber-500"
                  value={isDebit}
                  onChange={(e) => {
                    const val = Number(e.target.value);
                    setIsDebit(val);
                    if (val === IS_DEBIT.DEBIT) {
                      setPaymentMethod(PAYMENT_METHOD.CONG_NO);
                    } else if (paymentMethod === PAYMENT_METHOD.CONG_NO) {
                      setPaymentMethod(PAYMENT_METHOD.CASH);
                    }
                  }}
                >
                  <option value={IS_DEBIT.NO_DEBIT}>Thanh toán ngay</option>
                  <option value={IS_DEBIT.DEBIT}>Ghi nợ</option>
                </select>
              </div>
            </div>
          </div>

          {/* Weight Section */}
          <div className="bg-blue-50 rounded-lg p-4 border border-blue-100">
            <h4 className="text-sm font-semibold text-blue-800 mb-3">Thông tin cân</h4>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm text-slate-600 mb-1">
                  Số ký khi nhận
                  <span className="text-xs text-slate-400 ml-1">(cân lần 1)</span>
                </label>
                <input
                  type="number"
                  step="0.1"
                  min="0"
                  value={pickupWeight}
                  onChange={(e) => setPickupWeight(e.target.value)}
                  placeholder="Nhập số ký"
                  className="w-full border rounded-lg px-3 py-2 focus:outline-none focus:ring-1 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm text-slate-600 mb-1">
                  Số ký sau giặt
                  <span className="text-xs text-slate-400 ml-1">(cân lần 2)</span>
                </label>
                <input
                  type="number"
                  step="0.1"
                  min="0"
                  value={finalWeight}
                  onChange={(e) => setFinalWeight(e.target.value)}
                  placeholder="Nhập số ký"
                  className="w-full border rounded-lg px-3 py-2 focus:outline-none focus:ring-1 focus:ring-green-500"
                />
              </div>
            </div>
            {(order.final_weight || order.pickup_weight) ? (
              <div className="mt-3 text-sm text-slate-600">
                <span className="font-medium">Số ký hiện tại:</span>{" "}
                {order.final_weight ? (
                  <span className="text-green-600 font-semibold">{formatKg(order.final_weight)} kg (sau giặt)</span>
                ) : (
                  <span className="text-blue-600 font-semibold">{formatKg(order.pickup_weight)} kg (khi nhận)</span>
                )}
              </div>
            ) : null}
          </div>

          {/* Phân công nhân viên */}
          <div className="bg-green-50 rounded-lg p-4 border border-green-100">
            <h4 className="text-sm font-semibold text-green-800 mb-3">Phân công nhân viên</h4>
            <div className="grid grid-cols-2 gap-3">
              {/* Đơn kiểm đếm: NV kiểm đếm khi NHẬN (với khách) — trước bước lấy hàng */}
              {isCounting && (
                <div>
                  <label className="block text-xs text-slate-500 mb-1">NV kiểm đếm (khi nhận)</label>
                  <PickupStaffSelect
                    branchId={staffBranchId}
                    value={selectedCounters}
                    onChange={(selected) => setSelectedCounters(selected)}
                    placeholder="Chọn NV kiểm đếm..."
                    filterRole="counter"
                    disabled={isLocked(9)}
                  />
                  {lockHint(9)}
                </div>
              )}

              {/* Lấy hàng — xe máy (chọn tài xế) hoặc xe tải (đội hình xe) */}
              <div>
                <label className="block text-xs text-slate-500 mb-1">
                  {isCounting ? "Tài xế lấy hàng" : "Lấy hàng"}
                </label>
                <VehicleLegPicker
                  label="Phương tiện"
                  value={pickupVehicleId}
                  onChange={(id) => {
                    setPickupVehicleId(id);
                    // Đổi xe / về xe máy → bỏ người đang chọn (là đội xe cũ), tránh gán lại họ như tài xế thường.
                    setSelectedPickups([]);
                  }}
                  disabled={!pickupLegOpen || isLocked(1)}
                  disabledHint="Chiều lấy hàng đã có người nhận — không đổi phương tiện được"
                />
                {!pickupVehicleId && (
                  <div className="mt-2">
                    <PickupStaffSelect
                      branchId={staffBranchId}
                      value={selectedPickups}
                      onChange={(selected) => setSelectedPickups(selected)}
                      placeholder="Chọn tài xế lấy hàng..."
                      filterRole="driver"
                      disabled={isLocked(1)}
                    />
                  </div>
                )}
                {lockHint(1)}
              </div>

              {/* Giặt ủi (simple) hoặc Giặt (extended) */}
              {!isExtended ? (
                <div>
                  <label className="block text-xs text-slate-500 mb-1">Giặt ủi</label>
                  <SelectDropdownAsyncPaginate<EmployeeOption>
                    key={`laundry-${order.id}`}
                    url={`${API_USER_ROUTER.API_PRODUCTION_STAFF}`}
                    projectId={ApiConfig.PROJECT_ID.USER_SERVICE}
                    placeholder="Chọn NV giặt ủi..."
                    isMulti={true}
                    defaultOptions={selectedEmployees}
                    onChange={(selected) => setSelectedEmployees(selected)}
                    labelOption="name"
                    classNames="w-full"
                    params={{ branch_id: staffBranchId }}
                    disabled={isLocked(2)}
                  />
                  {lockHint(2)}
                </div>
              ) : (
                <div>
                  <label className="block text-xs text-slate-500 mb-1">Giặt</label>
                  <SelectDropdownAsyncPaginate<EmployeeOption>
                    key={`washer-${order.id}`}
                    url={`${API_USER_ROUTER.API_PRODUCTION_STAFF}`}
                    projectId={ApiConfig.PROJECT_ID.USER_SERVICE}
                    placeholder="Chọn NV giặt..."
                    isMulti={true}
                    defaultOptions={selectedWashers}
                    onChange={(selected) => setSelectedWashers(selected)}
                    labelOption="name"
                    classNames="w-full"
                    params={{ branch_id: staffBranchId }}
                    disabled={isLocked(5)}
                  />
                  {lockHint(5)}
                </div>
              )}

              {/* Extended: Sấy + Ủi */}
              {isExtended && (
                <>
                  <div>
                    <label className="block text-xs text-slate-500 mb-1">Sấy</label>
                    <SelectDropdownAsyncPaginate<EmployeeOption>
                      key={`dryer-${order.id}`}
                      url={`${API_USER_ROUTER.API_PRODUCTION_STAFF}`}
                      projectId={ApiConfig.PROJECT_ID.USER_SERVICE}
                      placeholder="Chọn NV sấy..."
                      isMulti={true}
                      defaultOptions={selectedDryers}
                      onChange={(selected) => setSelectedDryers(selected)}
                      labelOption="name"
                      classNames="w-full"
                      params={{ branch_id: staffBranchId }}
                      disabled={isLocked(6)}
                    />
                    {lockHint(6)}
                  </div>
                  <div>
                    <label className="block text-xs text-slate-500 mb-1">Ủi</label>
                    <SelectDropdownAsyncPaginate<EmployeeOption>
                      key={`ironer-${order.id}`}
                      url={`${API_USER_ROUTER.API_PRODUCTION_STAFF}`}
                      projectId={ApiConfig.PROJECT_ID.USER_SERVICE}
                      placeholder="Chọn NV ủi..."
                      isMulti={true}
                      defaultOptions={selectedIroners}
                      onChange={(selected) => setSelectedIroners(selected)}
                      labelOption="name"
                      classNames="w-full"
                      params={{ branch_id: staffBranchId }}
                      disabled={isLocked(7)}
                    />
                    {lockHint(7)}
                  </div>
                  <div>
                    <label className="block text-xs text-slate-500 mb-1">Gấp</label>
                    <SelectDropdownAsyncPaginate<EmployeeOption>
                      key={`folder-${order.id}`}
                      url={`${API_USER_ROUTER.API_PRODUCTION_STAFF}`}
                      projectId={ApiConfig.PROJECT_ID.USER_SERVICE}
                      placeholder="Chọn NV gấp..."
                      isMulti={true}
                      defaultOptions={selectedFolders}
                      onChange={(selected) => setSelectedFolders(selected)}
                      labelOption="name"
                      classNames="w-full"
                      params={{ branch_id: staffBranchId }}
                      disabled={isLocked(8)}
                    />
                    {lockHint(8)}
                  </div>
                </>
              )}

              {/* Giao hàng — xe máy (chọn tài xế) hoặc xe tải (đội hình xe) */}
              <div>
                <label className="block text-xs text-slate-500 mb-1">
                  {isCounting ? "Tài xế giao hàng" : "Giao hàng"}
                </label>
                <VehicleLegPicker
                  label="Phương tiện"
                  value={deliveryVehicleId}
                  onChange={(id) => {
                    setDeliveryVehicleId(id);
                    setSelectedDeliveries([]);
                  }}
                  disabled={!deliveryLegOpen || isLocked(3)}
                  disabledHint="Chiều giao hàng đã bắt đầu — không đổi phương tiện được"
                />
                {!deliveryVehicleId && (
                  <div className="mt-2">
                    <PickupStaffSelect
                      branchId={staffBranchId}
                      value={selectedDeliveries}
                      onChange={(selected) => setSelectedDeliveries(selected)}
                      placeholder="Chọn tài xế giao hàng..."
                      filterRole="driver"
                      disabled={isLocked(3)}
                    />
                  </div>
                )}
                {lockHint(3)}
              </div>

              {/* Đơn kiểm đếm: NV kiểm đếm khi GIAO (với khách) */}
              {isCounting && (
                <div>
                  <label className="block text-xs text-slate-500 mb-1">NV kiểm đếm (khi giao)</label>
                  <PickupStaffSelect
                    branchId={staffBranchId}
                    value={selectedDeliveryCounters}
                    onChange={(selected) => setSelectedDeliveryCounters(selected)}
                    placeholder="Chọn NV kiểm đếm..."
                    filterRole="counter"
                    disabled={isLocked(12)}
                  />
                  {lockHint(12)}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="mt-6 flex justify-end gap-3">
          <button
            onClick={onClose}
            className="px-4 py-2 border rounded-lg hover:bg-slate-50"
          >
            Hủy
          </button>
          <button
            // Đổi chi nhánh mà chưa chọn xong dịch vụ thì BE cũng từ chối — khoá luôn ở đây cho rõ.
            disabled={loading || (branchChanged && !branchItemsReady) || (itemsChanged && !editedItemsReady)}
            onClick={handleSave}
            title={
              branchChanged && !branchItemsReady
                ? "Chọn đủ dịch vụ của chi nhánh mới trước khi lưu"
                : itemsChanged && !editedItemsReady
                  ? "Kiểm tra lại sản phẩm / dịch vụ và số lượng trước khi lưu"
                  : undefined
            }
            className="px-5 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-60"
          >
            {loading ? "Đang lưu..." : "Lưu"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default OrderUpdateModal;
